import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { SiteSettings } from '../../types.ts';
import {
  ShieldCheck,
  Award,
  Zap,
  Star,
  Check,
  CheckCircle2,
  Plus,
  Trash2,
  HelpCircle,
  TrendingUp,
  FileText,
  Layers,
  ArrowRight,
  Sliders,
  Type,
  LayoutGrid,
  Image as ImageIcon,
  Video,
  Film,
  Play,
  Upload,
  Link,
  Volume2,
  VolumeX,
  Building2,
  MessageSquare,
  X,
  Info,
  Globe,
  Copy,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  HeartHandshake,
  Clock,
  ThumbsUp,
  Users,
  Compass,
  MapPin,
  Headphones,
} from 'lucide-react';

// Template bawaan 6 keunggulan standar
const DEFAULT_WHY_FEATURES = [
  {
    id: 'feat-1',
    icon: 'ShieldCheck',
    title: 'Legalitas Resmi & Berbadan Hukum',
    description:
      'Perusahaan terdaftar resmi dengan izin lengkap, memberikan rasa aman, transparansi kontrak, dan akuntabilitas dalam setiap kerja sama.',
    badge: '100% Aman & Terpercaya',
  },
  {
    id: 'feat-2',
    icon: 'Award',
    title: 'Fasilitator & Tim Ahli Tersertifikasi',
    description:
      'Dipandu oleh instruktur outbound bersertifikat BNSP, master game berpengalaman, serta tim kreatif digital yang andal di bidangnya.',
    badge: 'Instruktur BNSP',
  },
  {
    id: 'feat-3',
    icon: 'Zap',
    title: 'Peralatan & SOP Standar Internasional',
    description:
      'Seluruh perlengkapan teknis outdoor dan sistem operasional melalui proses inspeksi ketat mengutamakan keselamatan (Safety First).',
    badge: 'Safety First',
  },
  {
    id: 'feat-4',
    icon: 'Star',
    title: 'Konsep Fleksibel & Custom Sesuai Budget',
    description:
      'Paket kegiatan dan layanan digital dapat disesuaikan dengan tujuan (goals), jumlah peserta, tema acara, dan alokasi anggaran Anda.',
    badge: 'Paket Fleksibel',
  },
  {
    id: 'feat-5',
    icon: 'TrendingUp',
    title: 'Dukungan Ekosistem UMKM & Softselling',
    description:
      'Mengintegrasikan produk lokal bernilai tambah tinggi dalam setiap souvenir dan konsumsi event sebagai wujud nyata ekonomi berkelanjutan.',
    badge: 'Kurasi UMKM',
  },
  {
    id: 'feat-6',
    icon: 'Headphones',
    title: 'Konsultasi Gratis & Fast Response 24/7',
    description:
      'Tim customer service dan konsultan kami siap memberikan asistensi, pembuatan proposal instan, dan solusi cepat setiap saat.',
    badge: 'Respon Cepat',
  },
];

// Preset template siap pakai untuk penambahan kartu baru
const WHY_FEATURE_PRESETS = [
  {
    name: 'Keunggulan Standar',
    icon: 'CheckCircle2',
    title: 'Keunggulan Layanan & Kualitas Prima',
    badge: 'Kualitas Teruji',
    description: 'Jelaskan nilai lebih layanan atau produk Anda secara spesifik, terpercaya, dan menarik bagi calon klien di sini.',
  },
  {
    name: 'Legalitas & Izin',
    icon: 'ShieldCheck',
    title: 'Legalitas Resmi & Berbadan Hukum',
    badge: '100% Legal & Terpercaya',
    description: 'Perusahaan berbadan hukum resmi dengan perizinan operasional lengkap, menjamin keamanan dan transparansi kerja sama.',
  },
  {
    name: 'Fasilitator BNSP',
    icon: 'Award',
    title: 'Fasilitator & Instruktur Tersertifikasi BNSP',
    badge: 'Lisensi BNSP Resmi',
    description: 'Dipandu oleh instruktur berlisensi resmi dengan jam terbang tinggi dalam memandu event outbound skala nasional.',
  },
  {
    name: 'Standar Safety',
    icon: 'Zap',
    title: 'Peralatan & SOP Standar Keselamatan Tinggi',
    badge: 'Safety First',
    description: 'Peralatan teknis teruji berkala dengan prosedur SOP keselamatan ketat demi kenyamanan dan perlindungan peserta.',
  },
  {
    name: 'Custom Budget',
    icon: 'Star',
    title: 'Konsep Fleksibel Sesuai Alokasi Budget',
    badge: 'Paket Fleksibel',
    description: 'Rancangan acara, itinerary, dan fasilitas dapat disesuaikan fleksibel dengan kebutuhan serta alokasi anggaran Anda.',
  },
  {
    name: 'Layanan 24/7',
    icon: 'Headphones',
    title: 'Konsultasi Cepat & Respon Ramah 24 Jam',
    badge: 'Respon Cepat 24 Jam',
    description: 'Tim konsultan kami siap membantu penyusunan RAB, proposal kegiatan, dan respon cepat via WhatsApp.',
  },
  {
    name: 'Rating Bintang 5',
    icon: 'Star',
    title: 'Tingkat Kepuasan Klien 99% Ulasan Positif',
    badge: 'Rating Bintang 5',
    description: 'Telah dipercaya oleh ratusan perusahaan ternama, instansi BUMN, kementerian, dan komunitas di seluruh Indonesia.',
  },
  {
    name: 'Kemitraan Amanah',
    icon: 'HeartHandshake',
    title: 'Kemitraan Jangka Panjang & Terpercaya',
    badge: 'Mitra Amanah',
    description: 'Menjunjung tinggi profesionalisme, akuntabilitas, serta kepuasan menyeluruh di setiap proyek kegiatan bersama Anda.',
  },
];

export default function SEOWhyChooseUsManager() {
  const { settings, updateSettingsOnServer, showToast } = useApp();
  const primaryColor = settings?.theme?.primaryColor || '#059669';
  const [lastSavedTime, setLastSavedTime] = useState<string | null>(null);

  // Hero Headline State
  const [staticHero, setStaticHero] = useState({
    mediaType: (settings?.heroHeadline?.mediaType || 'image') as 'image' | 'gallery' | 'video',
    staticTitle:
      settings?.heroHeadline?.staticTitle ||
      'Solusi Terpadu Event Outbound, Digital Agency & Marketplace UMKM Terbaik di Indonesia',
    staticSubtitle:
      settings?.heroHeadline?.staticSubtitle ||
      'Partner strategis terpercaya untuk team building corporate, branding portofolio modern, serta pemberdayaan produk lokal unggulan dengan standar profesional.',
    staticBadge: settings?.heroHeadline?.staticBadge || 'Pilihan Nomor #1 di Indonesia',
    staticCategory: settings?.heroHeadline?.staticCategory || 'Official Partner',
    staticCoverImage:
      settings?.heroHeadline?.staticCoverImage ||
      'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1600&q=80',
    galleryImages:
      settings?.heroHeadline?.galleryImages && settings.heroHeadline.galleryImages.length > 0
        ? settings.heroHeadline.galleryImages
        : [
            settings?.heroHeadline?.staticCoverImage ||
              'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1600&q=80',
            'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1600&q=80',
            'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=1600&q=80',
          ],
    videoUrl: settings?.heroHeadline?.videoUrl || 'https://www.youtube.com/watch?v=kJQP7kiw5Fk',
    videoType: (settings?.heroHeadline?.videoType || 'youtube') as 'youtube' | 'mp4',
    videoAutoplay: settings?.heroHeadline?.videoAutoplay !== false,
    videoMuted: settings?.heroHeadline?.videoMuted !== false,
    videoLoop: settings?.heroHeadline?.videoLoop !== false,
    sliderInterval: settings?.heroHeadline?.sliderInterval || 5,
    staticCtaText:
      settings?.heroHeadline?.staticCtaText && settings.heroHeadline.staticCtaText !== 'Konsultasi & Reservasi Sekarang'
        ? settings.heroHeadline.staticCtaText
        : 'Lihat Katalog Paket & Layanan',
    staticCtaLink:
      settings?.heroHeadline?.staticCtaLink &&
      settings.heroHeadline.staticCtaLink !==
        'https://wa.me/6281234567890?text=Halo%20Admin,%20saya%20tertarik%20dengan%20layanan%20Nusantara%20Sinergi%20Kreatif'
        ? settings.heroHeadline.staticCtaLink
        : 'products',
    showHighlights: settings?.heroHeadline?.showHighlights !== false,
    highlights:
      settings?.heroHeadline?.highlights && settings.heroHeadline.highlights.length > 0
        ? settings.heroHeadline.highlights
        : [
            { id: 'hl-1', icon: 'ShieldCheck', title: 'Resmi & Terpercaya', subtitle: 'Legalitas Lengkap', enabled: true },
            { id: 'hl-2', icon: 'Award', title: 'Standar Profesional', subtitle: 'Tim Berpengalaman', enabled: true },
            { id: 'hl-3', icon: 'Headphones', title: 'Respon Cepat 24/7', subtitle: 'Konsultasi Ramah', enabled: true },
            { id: 'hl-4', icon: 'CheckCircle2', title: 'Garansi Kualitas', subtitle: 'Kepuasan Pelanggan', enabled: true },
          ],
  });

  // Default client logos list
  const defaultClientLogos = [
    { id: 'logo-1', name: 'PT Nusantara Energi', category: 'BUMN & Energi', logoUrl: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?auto=format&fit=crop&w=300&q=80' },
    { id: 'logo-2', name: 'Bank Mandiri Corporate', category: 'Perbankan & Keuangan', logoUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=300&q=80' },
    { id: 'logo-3', name: 'Telkom Indonesia Group', category: 'Telekomunikasi', logoUrl: 'https://images.unsplash.com/photo-1516876437184-593fda40c7ce?auto=format&fit=crop&w=300&q=80' },
    { id: 'logo-4', name: 'Pertamina Patra Niaga', category: 'BUMN Migas', logoUrl: 'https://images.unsplash.com/photo-1542744094-3a31b272c490?auto=format&fit=crop&w=300&q=80' },
    { id: 'logo-5', name: 'Kuliner Rempah Nusantara', category: 'F&B & Ekspor UMKM', logoUrl: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=300&q=80' },
    { id: 'logo-6', name: 'Dinas Pariwisata & Kebudayaan', category: 'Instansi Pemerintah', logoUrl: 'https://images.unsplash.com/photo-1572021335469-31706a17aaef?auto=format&fit=crop&w=300&q=80' },
    { id: 'logo-7', name: 'PT PLN (Persero)', category: 'Kelistrikan Nasional', logoUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=300&q=80' },
    { id: 'logo-8', name: 'Astra International', category: 'Otomotif & Industri', logoUrl: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?auto=format&fit=crop&w=300&q=80' },
  ];

  // Testimonial & Client Logo Admin State
  const [testiConfig, setTestiConfig] = useState({
    displayMode: (settings?.testimonialsConfig?.displayMode || 'both') as 'reviews' | 'logos' | 'both',
    badge: settings?.testimonialsConfig?.badge || 'Dipercaya 500+ Klien',
    title: settings?.testimonialsConfig?.title || 'Apa Kata Klien & Mitra Perusahaan Kami',
    subtitle: settings?.testimonialsConfig?.subtitle || 'Testimoni & logo instansi, BUMN, serta perusahaan swasta terkemuka yang telah bekerja sama',
    clientLogos: settings?.testimonialsConfig?.clientLogos?.length ? settings.testimonialsConfig.clientLogos : defaultClientLogos,
  });

  // State for new gallery image input
  const [newGalleryInput, setNewGalleryInput] = useState('');
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);

  // Why Choose Us State
  const [whyConfig, setWhyConfig] = useState(() => {
    return (
      settings?.whyChooseUs || {
        badge: 'MENGAPA MEMILIH KAMI • VALUE & TRUST',
        title: 'Keunggulan Utama & Alasan Mempercayakan Event serta Bisnis Anda Kepada Kami',
        subtitle:
          'Kombinasi legalitas resmi, instruktur tersertifikasi, dan teknologi modern untuk memberikan hasil optimal bergaransi kepuasan 100%.',
        seoKeywordsIntro:
          'Sebagai penyedia jasa event organizer gathering, outbound adventure, agensi portofolio kreatif, dan kurator marketplace UMKM terkemuka, kami menghadirkan standar mutu tinggi yang dirancang khusus untuk memenuhi kebutuhan instansi pemerintahan, perusahaan multinasional, komunitas, hingga pelaku usaha lokal di seluruh Indonesia.',
        features: [
          {
            id: 'feat-1',
            icon: 'ShieldCheck',
            title: 'Legalitas Resmi & Berbadan Hukum',
            description:
              'Perusahaan terdaftar resmi dengan izin lengkap, memberikan rasa aman, transparansi kontrak, dan akuntabilitas dalam setiap kerja sama.',
            badge: '100% Aman & Terpercaya',
          },
          {
            id: 'feat-2',
            icon: 'Award',
            title: 'Fasilitator & Tim Ahli Tersertifikasi',
            description:
              'Dipandu oleh instruktur outbound bersertifikat BNSP, master game berpengalaman, serta tim kreatif digital yang andal di bidangnya.',
            badge: 'Instruktur BNSP',
          },
          {
            id: 'feat-3',
            icon: 'Zap',
            title: 'Peralatan & SOP Standar Internasional',
            description:
              'Seluruh perlengkapan teknis outdoor dan sistem operasional melalui proses inspeksi ketat mengutamakan keselamatan (Safety First).',
            badge: 'Safety First',
          },
          {
            id: 'feat-4',
            icon: 'Star',
            title: 'Konsep Fleksibel & Custom Sesuai Budget',
            description:
              'Paket kegiatan dan layanan digital dapat disesuaikan dengan tujuan (goals), jumlah peserta, tema acara, dan alokasi anggaran Anda.',
            badge: 'Paket Fleksibel',
          },
          {
            id: 'feat-5',
            icon: 'TrendingUp',
            title: 'Dukungan Ekosistem UMKM & Softselling',
            description:
              'Mengintegrasikan produk lokal bernilai tambah tinggi dalam setiap souvenir dan konsumsi event sebagai wujud nyata ekonomi berkelanjutan.',
            badge: 'Kurasi UMKM',
          },
          {
            id: 'feat-6',
            icon: 'Headphones',
            title: 'Konsultasi Gratis & Fast Response 24/7',
            description:
              'Tim customer service dan konsultan kami siap memberikan asistensi, pembuatan proposal instan, dan solusi cepat setiap saat.',
            badge: 'Respon Cepat',
          },
        ],
        richSeoContent:
          'Kami memahami bahwa keberhasilan sebuah event gathering, outbound team building, ataupun peluncuran produk digital membutuhkan persiapan yang matang dan eksekusi tanpa celah. Dengan rekam jejak lebih dari ratusan proyek sukses di berbagai kota (Bandung, Jakarta, Bogor, Yogyakarta, Bali), Nusantara Sinergi Kreatif menjadi mitra ideal bagi Anda yang memprioritaskan kualitas, estetika, efisiensi anggaran, dan dampak positif jangka panjang.',
        ctaTitle: 'Siap Merencanakan Event Terbaik atau Mengembangkan Bisnis Anda?',
        ctaText: 'Hubungi Konsultan Kami Sekarang',
        ctaSubtext:
          'Dapatkan proposal penawaran harga dan itinerary eksklusif gratis dalam hitungan menit',
        ctaWaMessage: 'Halo Tim Konsultan, saya ingin berdiskusi dan menanyakan paket layanan serta keunggulan penawaran Anda.',
      }
    );
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.heroHeadline) {
      setStaticHero({
        mediaType: (settings.heroHeadline.mediaType || 'image') as any,
        staticTitle: settings.heroHeadline.staticTitle || staticHero.staticTitle,
        staticSubtitle: settings.heroHeadline.staticSubtitle || staticHero.staticSubtitle,
        staticBadge: settings.heroHeadline.staticBadge || staticHero.staticBadge,
        staticCategory: settings.heroHeadline.staticCategory || staticHero.staticCategory,
        staticCoverImage: settings.heroHeadline.staticCoverImage || staticHero.staticCoverImage,
        galleryImages:
          settings.heroHeadline.galleryImages && settings.heroHeadline.galleryImages.length > 0
            ? settings.heroHeadline.galleryImages
            : staticHero.galleryImages,
        videoUrl: settings.heroHeadline.videoUrl || staticHero.videoUrl,
        videoType: (settings.heroHeadline.videoType || 'youtube') as any,
        videoAutoplay: settings.heroHeadline.videoAutoplay !== false,
        videoMuted: settings.heroHeadline.videoMuted !== false,
        videoLoop: settings.heroHeadline.videoLoop !== false,
        sliderInterval: settings.heroHeadline.sliderInterval || 5,
        staticCtaText:
          settings.heroHeadline.staticCtaText && settings.heroHeadline.staticCtaText !== 'Konsultasi & Reservasi Sekarang'
            ? settings.heroHeadline.staticCtaText
            : (staticHero.staticCtaText || 'Lihat Katalog Paket & Layanan'),
        staticCtaLink:
          settings.heroHeadline.staticCtaLink &&
          settings.heroHeadline.staticCtaLink !==
            'https://wa.me/6281234567890?text=Halo%20Admin,%20saya%20tertarik%20dengan%20layanan%20Nusantara%20Sinergi%20Kreatif'
            ? settings.heroHeadline.staticCtaLink
            : (staticHero.staticCtaLink || 'products'),
        showHighlights: settings.heroHeadline.showHighlights !== false,
        highlights:
          settings.heroHeadline.highlights && settings.heroHeadline.highlights.length > 0
            ? settings.heroHeadline.highlights
            : staticHero.highlights,
      });
    }
    if (settings?.whyChooseUs) {
      setWhyConfig(settings.whyChooseUs);
    }
    if (settings?.testimonialsConfig) {
      setTestiConfig({
        displayMode: settings.testimonialsConfig.displayMode || 'both',
        badge: settings.testimonialsConfig.badge || 'Dipercaya 500+ Klien',
        title: settings.testimonialsConfig.title || 'Apa Kata Klien & Mitra Perusahaan Kami',
        subtitle: settings.testimonialsConfig.subtitle || 'Testimoni & logo instansi, BUMN, serta perusahaan swasta terkemuka yang telah bekerja sama',
        clientLogos: settings.testimonialsConfig.clientLogos?.length ? settings.testimonialsConfig.clientLogos : defaultClientLogos,
      });
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Partial<SiteSettings> = {
        heroHeadline: {
          mode: 'static',
          ...staticHero,
        },
        whyChooseUs: whyConfig,
        testimonialsConfig: testiConfig,
      };

      await updateSettingsOnServer(payload);
      const timeStr = new Date().toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      setLastSavedTime(timeStr);
      showToast('Headline, Keunggulan & Struktur SEO berhasil disimpan!', 'success');
    } catch (err) {
      showToast('Gagal menyimpan headline dan keunggulan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddFeature = (preset?: Partial<{ icon: string; title: string; badge: string; description: string }>) => {
    const newFeat = {
      id: `feat-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      icon: preset?.icon || 'CheckCircle2',
      title: preset?.title || 'Keunggulan Baru Layanan',
      description: preset?.description || 'Jelaskan nilai lebih layanan atau produk Anda secara spesifik dan menarik di sini.',
      badge: preset?.badge || 'Nilai Tambah',
    };
    setWhyConfig((prev) => ({
      ...prev,
      features: [...(prev.features || []), newFeat],
    }));
    showToast(`Kartu "${newFeat.title}" berhasil ditambahkan!`, 'success');
  };

  const handleDuplicateFeature = (index: number) => {
    const list = [...(whyConfig.features || [])];
    const source = list[index];
    if (!source) return;
    const duplicated = {
      ...source,
      id: `feat-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      title: `${source.title} (Salinan)`,
    };
    list.splice(index + 1, 0, duplicated);
    setWhyConfig((prev) => ({ ...prev, features: list }));
    showToast(`Kartu keunggulan berhasil diduplikasi!`, 'success');
  };

  const handleMoveFeature = (index: number, direction: 'up' | 'down') => {
    const list = [...(whyConfig.features || [])];
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= list.length) return;
    const temp = list[index];
    list[index] = list[targetIdx];
    list[targetIdx] = temp;
    setWhyConfig((prev) => ({ ...prev, features: list }));
  };

  const handleResetDefaultFeatures = () => {
    const confirmReset = window.confirm(
      'Apakah Anda ingin memulihkan 6 template kartu keunggulan standar bawaan sistem? Kartu saat ini akan digantikan dengan template resmi.'
    );
    if (!confirmReset) return;
    setWhyConfig((prev) => ({
      ...prev,
      features: DEFAULT_WHY_FEATURES,
    }));
    showToast('6 template kartu keunggulan standar berhasil dipulihkan!', 'success');
  };

  const handleUpdateFeature = (index: number, field: string, value: string) => {
    const updated = [...(whyConfig.features || [])];
    updated[index] = { ...updated[index], [field]: value };
    setWhyConfig({ ...whyConfig, features: updated });
  };

  const handleRemoveFeature = (index: number) => {
    const target = (whyConfig.features || [])[index];
    const updated = (whyConfig.features || []).filter((_, i) => i !== index);
    setWhyConfig({ ...whyConfig, features: updated });
    showToast(`Kartu "${target?.title || 'Keunggulan'}" berhasil dihapus.`, 'info');
  };

  const handleAddGalleryImage = () => {
    if (!newGalleryInput.trim()) return;
    setStaticHero((prev) => ({
      ...prev,
      galleryImages: [...prev.galleryImages, newGalleryInput.trim()],
    }));
    setNewGalleryInput('');
    showToast('Foto ditambahkan ke daftar slideshow!', 'success');
  };

  const handleRemoveGalleryImage = (index: number) => {
    setStaticHero((prev) => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((_, i) => i !== index),
    }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, target: 'single' | 'gallery') => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingMedia(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success && data.url) {
        if (target === 'single') {
          setStaticHero((prev) => ({ ...prev, staticCoverImage: data.url }));
        } else {
          setStaticHero((prev) => ({
            ...prev,
            galleryImages: [...prev.galleryImages, data.url],
          }));
        }
        showToast('Gambar berhasil diunggah!', 'success');
      } else {
        showToast(data.message || 'Gagal mengunggah gambar', 'error');
      }
    } catch (err: any) {
      showToast('Terjadi kesalahan saat unggah: ' + err.message, 'error');
    } finally {
      setIsUploadingMedia(false);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Header & Save Action */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-slate-900">
              Sorotan Headline (Foto/Video) & Struktur SEO Beranda
            </h2>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Atur media headline homepage (1 foto, multi-foto slideshow, atau 1 video YouTube), teks Tag H1, subjudul H2, dan kartu keunggulan H3.
          </p>
        </div>

        <div className="flex flex-col items-end gap-1.5 self-start sm:self-auto">
          <button
            id="save-hero-why-btn"
            disabled={saving}
            onClick={handleSave}
            className="px-5 py-2.5 rounded-xl text-white font-bold text-xs shadow-md flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 transition-all cursor-pointer"
            style={{ backgroundColor: primaryColor }}
          >
            {saving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>Simpan Perubahan Headline</span>
              </>
            )}
          </button>

          {lastSavedTime && (
            <div
              id="hero-why-saved-notice"
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-300 rounded-xl text-emerald-900 text-xs font-bold animate-in fade-in slide-in-from-top-1"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
              <span>Berhasil disimpan ke database</span>
              <span className="text-[10px] text-emerald-700 font-mono font-medium">({lastSavedTime})</span>
            </div>
          )}
        </div>
      </div>

      {/* SEO PYRAMID EXPLANATION BANNER */}
      <div className="p-4 rounded-2xl bg-slate-900 text-white border border-slate-800 space-y-2">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-blue-400" />
          <h4 className="text-xs font-bold uppercase tracking-wider text-blue-300">
            Panduan Struktur Heading SEO Googlebot (H1 • H2 • H3)
          </h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs pt-1">
          <div className="p-2.5 bg-slate-800/80 rounded-xl border border-slate-700">
            <span className="text-amber-400 font-black block text-[11px] uppercase">🏷️ Tag H1 (Utama)</span>
            <span className="text-slate-300 text-[11px]">Judul Headline Utama di bawah ini. Cukup 1x per halaman agar Googlebot memfokuskan kata kunci nomor #1 bisnis Anda.</span>
          </div>
          <div className="p-2.5 bg-slate-800/80 rounded-xl border border-slate-700">
            <span className="text-blue-400 font-black block text-[11px] uppercase">🏷️ Tag H2 (Subjudul)</span>
            <span className="text-slate-300 text-[11px]">Judul Seksi Keunggulan & Mengapa Memilih Kami, Katalog Produk, Portofolio, dan Artikel Blog.</span>
          </div>
          <div className="p-2.5 bg-slate-800/80 rounded-xl border border-slate-700">
            <span className="text-white font-black block text-[11px] uppercase">🏷️ Tag H3 (Item / Fitur)</span>
            <span className="text-slate-300 text-[11px]">Judul masing-masing kartu keunggulan, kartu produk, dan kartu layanan spesifik.</span>
          </div>
        </div>
      </div>

      {/* SECTION 1: HERO HEADLINE MEDIA & CONTENT */}
      <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-6">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">
                1
              </span>
              <h3 className="text-base font-bold text-slate-900">
                Pilihan Media Background Headline (Foto / Slideshow / Video)
              </h3>
            </div>
            <p className="text-xs text-slate-500 pl-8">
              Pilih apakah Anda ingin menampilkan 1 Foto statis, Beberapa Foto (Slideshow multi-foto), atau 1 Video interaktif pada bagian grid headline homepage.
            </p>
          </div>
        </div>

        {/* Media Selector Tabs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => setStaticHero({ ...staticHero, mediaType: 'image' })}
            className={`p-4 rounded-xl border flex items-center gap-3 text-left transition-all cursor-pointer ${
              staticHero.mediaType === 'image'
                ? 'bg-blue-50/80 border-blue-500 ring-2 ring-blue-500/20 text-blue-950 font-bold'
                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
            }`}
          >
            <div className={`p-2.5 rounded-lg ${staticHero.mediaType === 'image' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
              <ImageIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs font-bold">1 Foto Statis</div>
              <div className="text-[11px] text-slate-500 font-normal">Satu foto cover banner berkualitas tinggi</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setStaticHero({ ...staticHero, mediaType: 'gallery' })}
            className={`p-4 rounded-xl border flex items-center gap-3 text-left transition-all cursor-pointer ${
              staticHero.mediaType === 'gallery'
                ? 'bg-blue-50/80 border-blue-500 ring-2 ring-blue-500/20 text-blue-950 font-bold'
                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
            }`}
          >
            <div className={`p-2.5 rounded-lg ${staticHero.mediaType === 'gallery' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs font-bold">Beberapa Foto (Slideshow)</div>
              <div className="text-[11px] text-slate-500 font-normal">Transisi multi-foto dengan tombol slider</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setStaticHero({ ...staticHero, mediaType: 'video' })}
            className={`p-4 rounded-xl border flex items-center gap-3 text-left transition-all cursor-pointer ${
              staticHero.mediaType === 'video'
                ? 'bg-blue-50/80 border-blue-500 ring-2 ring-blue-500/20 text-blue-950 font-bold'
                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
            }`}
          >
            <div className={`p-2.5 rounded-lg ${staticHero.mediaType === 'video' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
              <Video className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs font-bold">1 Video Interaktif</div>
              <div className="text-[11px] text-slate-500 font-normal">Video YouTube atau direct file MP4</div>
            </div>
          </button>
        </div>

        {/* Dynamic Media Input Configuration Panels */}
        {staticHero.mediaType === 'image' && (
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
            <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
              <ImageIcon className="w-4 h-4 text-blue-600" />
              <span>Konfigurasi 1 Foto Background:</span>
            </h4>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <input
                type="text"
                value={staticHero.staticCoverImage}
                onChange={(e) => setStaticHero({ ...staticHero, staticCoverImage: e.target.value })}
                placeholder="https://images.unsplash.com/..."
                className="flex-1 px-3.5 py-2.5 text-xs bg-white border border-slate-300 rounded-xl font-mono text-slate-700"
              />
              <label className="cursor-pointer px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-colors shrink-0">
                <Upload className="w-4 h-4" />
                <span>{isUploadingMedia ? 'Mengunggah...' : 'Unggah Foto'}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'single')}
                  disabled={isUploadingMedia}
                  className="hidden"
                />
              </label>
            </div>
            {staticHero.staticCoverImage && (
              <div className="relative w-full h-36 rounded-xl overflow-hidden border border-slate-300">
                <img
                  src={staticHero.staticCoverImage}
                  alt="Preview Single Cover"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-3">
                  <span className="text-[11px] text-white font-bold">Pratinjau Foto Headline</span>
                </div>
              </div>
            )}
          </div>
        )}

        {staticHero.mediaType === 'gallery' && (
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-blue-600" />
                <span>Daftar Multi-Foto Slideshow Headline (Total: {staticHero.galleryImages.length} Foto):</span>
              </h4>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-600 font-medium">Ganti slide tiap:</span>
                <select
                  value={staticHero.sliderInterval}
                  onChange={(e) => setStaticHero({ ...staticHero, sliderInterval: Number(e.target.value) })}
                  className="px-2.5 py-1 text-xs bg-white border border-slate-300 rounded-lg font-bold"
                >
                  <option value={3}>3 Detik</option>
                  <option value={5}>5 Detik (Standar)</option>
                  <option value={7}>7 Detik</option>
                  <option value={10}>10 Detik</option>
                </select>
              </div>
            </div>

            {/* Add Image input */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <input
                type="text"
                value={newGalleryInput}
                onChange={(e) => setNewGalleryInput(e.target.value)}
                placeholder="Masukkan URL foto baru (https://...)"
                className="flex-1 px-3.5 py-2 text-xs bg-white border border-slate-300 rounded-xl font-mono text-slate-700"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddGalleryImage();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleAddGalleryImage}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-colors shrink-0 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>Tambah URL</span>
              </button>
              <label className="cursor-pointer px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-colors shrink-0">
                <Upload className="w-4 h-4" />
                <span>{isUploadingMedia ? 'Upload...' : 'Unggah Foto'}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'gallery')}
                  disabled={isUploadingMedia}
                  className="hidden"
                />
              </label>
            </div>

            {/* List of Photos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {staticHero.galleryImages.map((imgUrl, idx) => (
                <div key={idx} className="relative group rounded-xl overflow-hidden border border-slate-200 bg-white shadow-2xs">
                  <div className="h-28 w-full bg-slate-100 overflow-hidden">
                    <img src={imgUrl} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-2 flex items-center justify-between bg-white">
                    <span className="text-[11px] font-bold text-slate-700 truncate max-w-[150px]">
                      Foto #{idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveGalleryImage(idx)}
                      className="p-1 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                      title="Hapus foto ini"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {staticHero.mediaType === 'video' && (
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
            <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
              <Video className="w-4 h-4 text-blue-600" />
              <span>Konfigurasi 1 Video Headline (YouTube / Link MP4):</span>
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  URL Video (Link YouTube atau direct file .mp4):
                </label>
                <input
                  type="text"
                  value={staticHero.videoUrl}
                  onChange={(e) => setStaticHero({ ...staticHero, videoUrl: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=... atau https://domain.com/video.mp4"
                  className="w-full px-3.5 py-2.5 text-xs bg-white border border-slate-300 rounded-xl font-mono text-slate-800"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Mendukung link YouTube standar (youtube.com/watch?v=...), tautan pendek (youtu.be/...), maupun direct link video .mp4.
                </p>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Tipe Video:</label>
                <select
                  value={staticHero.videoType}
                  onChange={(e) => setStaticHero({ ...staticHero, videoType: e.target.value as any })}
                  className="w-full px-3.5 py-2.5 text-xs bg-white border border-slate-300 rounded-xl font-bold"
                >
                  <option value="youtube">YouTube Embed (Otomatis & Ringan)</option>
                  <option value="mp4">File Direct MP4 Video</option>
                </select>
              </div>
            </div>

            {/* Video Playback Options */}
            <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-slate-200/80">
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={staticHero.videoAutoplay}
                  onChange={(e) => setStaticHero({ ...staticHero, videoAutoplay: e.target.checked })}
                  className="rounded text-blue-600"
                />
                <span>Putar Otomatis (Auto-play)</span>
              </label>

              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={staticHero.videoMuted}
                  onChange={(e) => setStaticHero({ ...staticHero, videoMuted: e.target.checked })}
                  className="rounded text-blue-600"
                />
                <span>Mulai Dalam Keadaan Senyap (Muted - Direkomendasikan untuk browser)</span>
              </label>

              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={staticHero.videoLoop}
                  onChange={(e) => setStaticHero({ ...staticHero, videoLoop: e.target.checked })}
                  className="rounded text-blue-600"
                />
                <span>Ulangi Terus (Looping Video)</span>
              </label>
            </div>
          </div>
        )}

        {/* Hero Text Customization with SEO Tags */}
        <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
            <Type className="w-4 h-4 text-blue-600" />
            <span>Kustomisasi Teks & Tag Heading SEO Hero</span>
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="text-xs font-black text-slate-900 block mb-1 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span>Judul Utama Headline Beranda</span>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-black rounded">
                    🏷️ Tag SEO: H1 (Utama)
                  </span>
                </span>
                <span className="text-[10px] text-slate-400 font-bold">Target Ranking #1 Google</span>
              </label>
              <input
                type="text"
                value={staticHero.staticTitle}
                onChange={(e) => setStaticHero({ ...staticHero, staticTitle: e.target.value })}
                placeholder="Contoh: Solusi Terpadu Event Outbound, Digital Agency & UMKM di Indonesia"
                className="w-full px-3.5 py-2.5 text-xs bg-white border border-slate-300 rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                *Teks ini dibaca oleh Googlebot sebagai Tag H1 utama beranda. Sertakan kata kunci bisnis utama Anda.
              </p>
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs font-black text-slate-900 block mb-1 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span>Sub-Judul / Paragraf Deskripsi Hero</span>
                  <span className="px-2 py-0.5 bg-slate-200 text-slate-800 text-[10px] font-bold rounded">
                    🏷️ Tag SEO: Meta Context
                  </span>
                </span>
                <span className="text-[10px] text-slate-400 font-bold">Variasi Keyword LSI</span>
              </label>
              <textarea
                rows={2}
                value={staticHero.staticSubtitle}
                onChange={(e) => setStaticHero({ ...staticHero, staticSubtitle: e.target.value })}
                placeholder="Deskripsi singkat yang meyakinkan calon klien..."
                className="w-full px-3.5 py-2.5 text-xs bg-white border border-slate-300 rounded-xl text-slate-800 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-900 block mb-1">Badge Sorotan:</label>
              <input
                type="text"
                value={staticHero.staticBadge}
                onChange={(e) => setStaticHero({ ...staticHero, staticBadge: e.target.value })}
                placeholder="Contoh: Pilihan Nomor #1 di Indonesia"
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-medium text-slate-800"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-900 block mb-1">Kategori / Label Samping:</label>
              <input
                type="text"
                value={staticHero.staticCategory}
                onChange={(e) => setStaticHero({ ...staticHero, staticCategory: e.target.value })}
                placeholder="Contoh: Official Partner"
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-medium text-slate-800"
              />
            </div>

            {/* Tombol Utama Headline (CTA Button Editor) */}
            <div className="sm:col-span-2 mt-2 p-4 bg-emerald-50/70 border border-emerald-200/80 rounded-2xl space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-emerald-200/60">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-xs shrink-0">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-black text-slate-900 tracking-tight flex items-center gap-2">
                      <span>🔘 Tombol Utama Headline (CTA)</span>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-extrabold rounded-md">
                        Tombol "Lihat Katalog Paket & Layanan"
                      </span>
                    </h4>
                    <p className="text-[11px] text-slate-500 font-normal">
                      Kustomisasi tulisan tombol dan tautan URL tujuan pada banner beranda utama.
                    </p>
                  </div>
                </div>

                {/* Preview Mini Badge */}
                <div className="flex items-center gap-2 self-start sm:self-auto bg-white/90 border border-emerald-200 px-3 py-1.5 rounded-xl shadow-3xs">
                  <span className="text-[10px] font-bold text-slate-400">Pratinjau Tombol:</span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-800 bg-emerald-100/80 px-2.5 py-0.5 rounded-md">
                    <span>{staticHero.staticCtaText || 'Lihat Katalog Paket & Layanan'}</span>
                    <ArrowRight className="w-3 h-3 text-emerald-600" />
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* 1. Teks Tombol */}
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1.5 flex items-center gap-1.5">
                    <Type className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Teks Tulisan Tombol:</span>
                  </label>
                  <input
                    type="text"
                    value={staticHero.staticCtaText}
                    onChange={(e) => setStaticHero({ ...staticHero, staticCtaText: e.target.value })}
                    placeholder="Contoh: Lihat Katalog Paket & Layanan"
                    className="w-full px-3.5 py-2.5 text-xs bg-white border border-slate-300 rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-none shadow-3xs"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    Label teks yang muncul di tombol banner headline beranda.
                  </p>
                </div>

                {/* 2. URL Link Tujuan Kustom */}
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1.5 flex items-center gap-1.5">
                    <Link className="w-3.5 h-3.5 text-emerald-600" />
                    <span>URL Link Tujuan Kustom:</span>
                  </label>
                  <input
                    type="text"
                    value={staticHero.staticCtaLink}
                    onChange={(e) => setStaticHero({ ...staticHero, staticCtaLink: e.target.value })}
                    placeholder="Contoh: products, https://wa.me/..., atau https://..."
                    className="w-full px-3.5 py-2.5 text-xs bg-white border border-slate-300 rounded-xl font-mono text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-none shadow-3xs"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    Bisa URL web kustom (https://...), WhatsApp (https://wa.me/...), atau halaman (products, articles, contact).
                  </p>

                  {/* Tombol Cepat Pilihan URL */}
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className="text-[10px] text-slate-400 font-medium">Pilihan Cepat:</span>
                    <button
                      type="button"
                      onClick={() => setStaticHero({ ...staticHero, staticCtaLink: 'products' })}
                      className={`text-[10px] px-2 py-0.5 rounded-md font-semibold transition-all cursor-pointer ${
                        staticHero.staticCtaLink === 'products'
                          ? 'bg-emerald-600 text-white shadow-3xs'
                          : 'bg-white text-slate-700 border border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                      }`}
                    >
                      Katalog Produk
                    </button>
                    <button
                      type="button"
                      onClick={() => setStaticHero({ ...staticHero, staticCtaLink: 'articles' })}
                      className={`text-[10px] px-2 py-0.5 rounded-md font-semibold transition-all cursor-pointer ${
                        staticHero.staticCtaLink === 'articles'
                          ? 'bg-emerald-600 text-white shadow-3xs'
                          : 'bg-white text-slate-700 border border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                      }`}
                    >
                      Blog & Artikel
                    </button>
                    <button
                      type="button"
                      onClick={() => setStaticHero({ ...staticHero, staticCtaLink: 'portfolio' })}
                      className={`text-[10px] px-2 py-0.5 rounded-md font-semibold transition-all cursor-pointer ${
                        staticHero.staticCtaLink === 'portfolio'
                          ? 'bg-emerald-600 text-white shadow-3xs'
                          : 'bg-white text-slate-700 border border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                      }`}
                    >
                      Portofolio
                    </button>
                    <button
                      type="button"
                      onClick={() => setStaticHero({ ...staticHero, staticCtaLink: 'contact' })}
                      className={`text-[10px] px-2 py-0.5 rounded-md font-semibold transition-all cursor-pointer ${
                        staticHero.staticCtaLink === 'contact'
                          ? 'bg-emerald-600 text-white shadow-3xs'
                          : 'bg-white text-slate-700 border border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                      }`}
                    >
                      Kontak
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* 4 Kartu Highlight Keunggulan Cepat di Bawah Banner Hero */}
            <div className="sm:col-span-2 mt-4 p-5 bg-slate-900 text-white rounded-2xl border border-slate-800 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-800">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-black text-white flex items-center gap-2">
                      <span>4 Kartu Highlight Keunggulan (Bilah Bawah Hero)</span>
                      <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] font-extrabold rounded-md border border-emerald-500/30">
                        Bilah 4 Kotak
                      </span>
                    </h4>
                    <p className="text-[11px] text-slate-400">
                      Pengaturan 4 kotak fitur yang tampil tepat di bawah banner foto/video headline beranda.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setStaticHero((prev) => ({ ...prev, showHighlights: !prev.showHighlights }))
                  }
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    staticHero.showHighlights
                      ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-xs'
                      : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                  }`}
                >
                  {staticHero.showHighlights ? '✓ Bilah Highlight Aktif' : '✕ Bilah Disembunyikan'}
                </button>
              </div>

              {staticHero.showHighlights && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {staticHero.highlights.map((hl, idx) => (
                      <div
                        key={hl.id || idx}
                        className="p-3.5 bg-slate-950/80 rounded-xl border border-slate-800 space-y-2.5"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black uppercase text-emerald-400 tracking-wider">
                            Kotak #{idx + 1}
                          </span>
                          <label className="flex items-center gap-1.5 text-[10px] text-slate-400 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={hl.enabled !== false}
                              onChange={(e) => {
                                const updated = [...staticHero.highlights];
                                updated[idx] = { ...updated[idx], enabled: e.target.checked };
                                setStaticHero((prev) => ({ ...prev, highlights: updated }));
                              }}
                              className="w-3.5 h-3.5 rounded text-emerald-600 border-slate-700"
                            />
                            <span>Tampilkan</span>
                          </label>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-bold text-slate-400 block mb-1">
                              Judul Utama:
                            </label>
                            <input
                              type="text"
                              value={hl.title}
                              onChange={(e) => {
                                const updated = [...staticHero.highlights];
                                updated[idx] = { ...updated[idx], title: e.target.value };
                                setStaticHero((prev) => ({ ...prev, highlights: updated }));
                              }}
                              placeholder="Misal: Resmi & Terpercaya"
                              className="w-full px-2.5 py-1.5 text-xs bg-slate-900 border border-slate-700 rounded-lg text-white font-bold focus:ring-1 focus:ring-emerald-500 outline-none"
                            />
                          </div>

                          <div>
                            <label className="text-[10px] font-bold text-slate-400 block mb-1">
                              Keterangan Singkat:
                            </label>
                            <input
                              type="text"
                              value={hl.subtitle}
                              onChange={(e) => {
                                const updated = [...staticHero.highlights];
                                updated[idx] = { ...updated[idx], subtitle: e.target.value };
                                setStaticHero((prev) => ({ ...prev, highlights: updated }));
                              }}
                              placeholder="Misal: Legalitas Lengkap"
                              className="w-full px-2.5 py-1.5 text-xs bg-slate-900 border border-slate-700 rounded-lg text-slate-300 focus:ring-1 focus:ring-emerald-500 outline-none"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-[10px] font-bold text-slate-400 block mb-1">
                            Pilihan Ikon:
                          </label>
                          <select
                            value={hl.icon || 'CheckCircle2'}
                            onChange={(e) => {
                              const updated = [...staticHero.highlights];
                              updated[idx] = { ...updated[idx], icon: e.target.value };
                              setStaticHero((prev) => ({ ...prev, highlights: updated }));
                            }}
                            className="w-full px-2.5 py-1.5 text-xs bg-slate-900 border border-slate-700 rounded-lg text-slate-200 focus:ring-1 focus:ring-emerald-500 outline-none"
                          >
                            <option value="ShieldCheck">🛡️ Pelindung / Legalitas (ShieldCheck)</option>
                            <option value="Award">🏅 Medali Penghargaan (Award)</option>
                            <option value="Headphones">🎧 Layanan / Customer Care (Headphones)</option>
                            <option value="CheckCircle2">✅ Centang Kualitas (CheckCircle2)</option>
                            <option value="ThumbsUp">👍 Jempol / Kepuasan (ThumbsUp)</option>
                            <option value="Zap">⚡ Kilat / Respon Cepat (Zap)</option>
                            <option value="none">🚫 Tanpa Ikon (Teks Saja)</option>
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pratinjau Tampilan */}
                  <div className="pt-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">
                      Pratinjau Tampilan di Homepage:
                    </span>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {staticHero.highlights.filter((h) => h.enabled !== false).map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center gap-2 p-2 rounded-xl bg-white/5 border border-white/10"
                        >
                          <div className="min-w-0">
                            <div className="text-[11px] font-bold text-white truncate">{item.title}</div>
                            <div className="text-[10px] text-slate-400 truncate">{item.subtitle}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2: WHY CHOOSE US / VALUE PROPOSITIONS MANAGER */}
      <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">
                2
              </span>
              <h3 className="text-base font-bold text-slate-900">
                Grid Keunggulan & Mengapa Memilih Kami (Value & Trust SEO)
              </h3>
            </div>
            <p className="text-xs text-slate-500 pl-8">
              Membangun kredibilitas, kepercayaan calon klien, dan memenangkan algoritma mesin pencari Google.
            </p>
          </div>

          <button
            type="button"
            onClick={() => handleAddFeature()}
            className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-2xs transition-colors shrink-0 cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Tambah Keunggulan</span>
          </button>
        </div>

        {/* Global SEO Intro & Titles */}
        <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
            <Sliders className="w-4 h-4 text-blue-600" />
            <span>Pengaturan Header Section Keunggulan</span>
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-900 block mb-1">Badge Kategori:</label>
              <input
                type="text"
                value={whyConfig.badge}
                onChange={(e) => setWhyConfig({ ...whyConfig, badge: e.target.value })}
                placeholder="MENGAPA MEMILIH KAMI • VALUE & TRUST"
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-bold text-slate-900"
              />
            </div>

            <div>
              <label className="text-xs font-black text-slate-900 block mb-1 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <span>Judul Utama Seksi Keunggulan</span>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-black rounded">
                    🏷️ Tag SEO: H2 (Subjudul)
                  </span>
                </span>
              </label>
              <input
                type="text"
                value={whyConfig.title}
                onChange={(e) => setWhyConfig({ ...whyConfig, title: e.target.value })}
                placeholder="Keunggulan Utama & Mengapa Memilih Kami"
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-bold text-slate-900"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs font-bold text-slate-900 block mb-1">Subtitle / Ringkasan:</label>
              <input
                type="text"
                value={whyConfig.subtitle}
                onChange={(e) => setWhyConfig({ ...whyConfig, subtitle: e.target.value })}
                placeholder="Komitmen profesional dan legalitas terpercaya..."
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl text-slate-800"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs font-bold text-slate-900 block mb-1 flex items-center justify-between">
                <span>Paragraf Pengantar Kaya Keyword (Keyword Density SEO):</span>
                <span className="text-[10px] text-blue-700 font-bold">Meningkatkan Search Ranking</span>
              </label>
              <textarea
                rows={3}
                value={whyConfig.seoKeywordsIntro || ''}
                onChange={(e) => setWhyConfig({ ...whyConfig, seoKeywordsIntro: e.target.value })}
                placeholder="Sebagai penyedia jasa event organizer gathering..."
                className="w-full px-3.5 py-2.5 text-xs bg-white border border-slate-300 rounded-xl leading-relaxed text-slate-800"
              />
            </div>
          </div>
        </div>

        {/* Feature Cards List with H3 Indicator */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-1 border-b border-slate-200">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-xs sm:text-sm font-black text-slate-800 uppercase tracking-wider">
                  Daftar Kartu Keunggulan (Tag SEO: H3 Sub-fitur)
                </h4>
                <span
                  className="px-2 py-0.5 rounded-full text-[11px] font-extrabold text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  {whyConfig.features?.length || 0} Kartu Aktif
                </span>
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Sesuaikan jumlah kartu keunggulan sesuai kebutuhan (bebas tambah, duplikat, atau hapus tanpa batas).
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleResetDefaultFeatures}
                className="px-3 py-1.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer"
                title="Pulihkan 6 template standar bawaan jika sebelumnya terhapus"
              >
                <RotateCcw className="w-3.5 h-3.5 text-slate-500" />
                <span>Pulihkan 6 Template Standar</span>
              </button>

              <button
                type="button"
                onClick={() => handleAddFeature()}
                className="px-3.5 py-1.5 rounded-xl text-white text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm hover:opacity-90 cursor-pointer"
                style={{ backgroundColor: primaryColor }}
              >
                <Plus className="w-3.5 h-3.5" />
                <span>+ Tambah Kartu Baru</span>
              </button>
            </div>
          </div>

          {/* Quick Preset Templates Bar */}
          <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
            <div className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5 mb-2">
              <Star className="w-3.5 h-3.5 text-amber-500" />
              <span>Tambah Cepat dengan Pilihan Template Populer:</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {WHY_FEATURE_PRESETS.map((preset, pIdx) => (
                <button
                  key={pIdx}
                  type="button"
                  onClick={() => handleAddFeature(preset)}
                  className="px-2.5 py-1 rounded-lg bg-white hover:bg-emerald-50 hover:border-emerald-300 border border-slate-200 text-slate-700 hover:text-emerald-800 text-[11px] font-semibold transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer"
                  title={preset.description}
                >
                  <Plus className="w-3 h-3 text-emerald-600" />
                  <span>{preset.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* If No Features Exist (Empty State) */}
          {(!whyConfig.features || whyConfig.features.length === 0) && (
            <div className="p-8 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/70 text-center space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center mx-auto shadow-2xs">
                <Info className="w-6 h-6" />
              </div>
              <h5 className="font-bold text-sm text-slate-800">
                Belum Ada Kartu Keunggulan
              </h5>
              <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
                Semua kartu keunggulan saat ini telah dihapus. Anda dapat menambahkan kartu keunggulan baru dengan template serupa atau memulihkan 6 template standar bawaan sistem.
              </p>
              <div className="flex items-center justify-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleResetDefaultFeatures}
                  className="px-4 py-2 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer"
                >
                  <RotateCcw className="w-4 h-4 text-slate-500" />
                  <span>Pulihkan 6 Template Standar</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleAddFeature()}
                  className="px-4 py-2 rounded-xl text-white text-xs font-bold transition-all flex items-center gap-1.5 shadow-md hover:opacity-90 cursor-pointer"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Plus className="w-4 h-4" />
                  <span>+ Buat Kartu Keunggulan Baru</span>
                </button>
              </div>
            </div>
          )}

          {/* Cards Grid */}
          {whyConfig.features && whyConfig.features.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {whyConfig.features.map((feat, idx) => (
                <div
                  key={feat.id || idx}
                  className="p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition-all shadow-2xs space-y-3 flex flex-col justify-between"
                >
                  <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded shrink-0">
                        SEO H3
                      </span>
                      <select
                        value={feat.icon}
                        onChange={(e) => handleUpdateFeature(idx, 'icon', e.target.value)}
                        className="px-2 py-1 text-xs bg-slate-50 border border-slate-300 rounded-lg font-bold text-slate-800"
                      >
                        <option value="ShieldCheck">🛡️ ShieldCheck (Legalitas/Aman)</option>
                        <option value="Award">🏆 Award (Sertifikat BNSP)</option>
                        <option value="Zap">⚡ Zap (Standar SOP/Safety)</option>
                        <option value="Star">⭐ Star (Kustom/Budget)</option>
                        <option value="TrendingUp">📈 TrendingUp (Ekosistem/UMKM)</option>
                        <option value="Headphones">🎧 Headphones (Respon 24/7)</option>
                        <option value="Star">⭐ Star (Bintang/Rating)</option>
                        <option value="HeartHandshake">🤝 HeartHandshake (Kemitraan)</option>
                        <option value="CheckCircle2">✅ CheckCircle2 (Kualitas)</option>
                        <option value="Clock">⏱️ Clock (Tepat Waktu)</option>
                        <option value="ThumbsUp">👍 ThumbsUp (Garansi Puas)</option>
                        <option value="Users">👥 Users (Tim & Instruktur)</option>
                        <option value="Compass">🧭 Compass (Petualangan)</option>
                        <option value="MapPin">📍 MapPin (Lokasi Strategis)</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => handleMoveFeature(idx, 'up')}
                        className={`p-1 rounded-lg transition-colors cursor-pointer ${
                          idx === 0
                            ? 'text-slate-300 cursor-not-allowed'
                            : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                        }`}
                        title="Pindah ke Atas / Urutan Sebelumnya"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>

                      <button
                        type="button"
                        disabled={idx === whyConfig.features!.length - 1}
                        onClick={() => handleMoveFeature(idx, 'down')}
                        className={`p-1 rounded-lg transition-colors cursor-pointer ${
                          idx === whyConfig.features!.length - 1
                            ? 'text-slate-300 cursor-not-allowed'
                            : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                        }`}
                        title="Pindah ke Bawah / Urutan Berikutnya"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDuplicateFeature(idx)}
                        className="p-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                        title="Duplikat Kartu Ini (Buat kartu dengan format serupa)"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleRemoveFeature(idx)}
                        className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                        title="Hapus Kartu Keunggulan Ini"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2.5 flex-1">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="sm:col-span-2">
                        <label className="text-[10px] font-bold text-slate-600 uppercase block mb-0.5">
                          Judul Keunggulan (Tag SEO: H3):
                        </label>
                        <input
                          type="text"
                          value={feat.title}
                          onChange={(e) => handleUpdateFeature(idx, 'title', e.target.value)}
                          placeholder="Contoh: Legalitas Resmi & Berbadan Hukum"
                          className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-lg font-bold text-slate-900 focus:bg-white transition-colors"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-600 uppercase block mb-0.5">
                          Badge Mini:
                        </label>
                        <input
                          type="text"
                          value={feat.badge || ''}
                          onChange={(e) => handleUpdateFeature(idx, 'badge', e.target.value)}
                          placeholder="Contoh: BNSP / 100% Legal"
                          className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-800 font-semibold focus:bg-white transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-600 uppercase block mb-0.5">
                        Deskripsi Penjelas:
                      </label>
                      <textarea
                        rows={2}
                        value={feat.description}
                        onChange={(e) => handleUpdateFeature(idx, 'description', e.target.value)}
                        placeholder="Jelaskan nilai lebih layanan atau keunggulan produk Anda..."
                        className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-lg font-medium text-slate-800 leading-relaxed focus:bg-white transition-colors"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Bottom Add More Button */}
          {whyConfig.features && whyConfig.features.length > 0 && (
            <button
              type="button"
              onClick={() => handleAddFeature()}
              className="w-full py-3 px-4 rounded-xl border-2 border-dashed border-slate-300 hover:border-emerald-500 hover:bg-emerald-50/40 text-slate-700 hover:text-emerald-800 font-bold text-xs transition-all flex items-center justify-center gap-2 cursor-pointer group shadow-2xs"
            >
              <div className="w-6 h-6 rounded-full bg-slate-200 group-hover:bg-emerald-200 text-slate-700 group-hover:text-emerald-800 flex items-center justify-center transition-colors">
                <Plus className="w-3.5 h-3.5" />
              </div>
              <span>+ Tambah Kartu Keunggulan Baru (Template Serupa)</span>
            </button>
          )}
        </div>

        {/* Rich SEO Content & CTA */}
        <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
            <FileText className="w-4 h-4 text-blue-600" />
            <span>Naskah Artikel SEO & Call To Action (Bottom Section)</span>
          </h4>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-slate-900 block mb-1">
                Judul Banner Utama (Banner CTA):
              </label>
              <input
                type="text"
                value={whyConfig.ctaTitle || ''}
                onChange={(e) => setWhyConfig({ ...whyConfig, ctaTitle: e.target.value })}
                placeholder="Siap Merencanakan Event Terbaik atau Mengembangkan Bisnis Anda?"
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl text-slate-800"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-900 block mb-1">
                Naskah Paragraf SEO Komprehensif (Penutup Seksi):
              </label>
              <textarea
                rows={4}
                value={whyConfig.richSeoContent || ''}
                onChange={(e) => setWhyConfig({ ...whyConfig, richSeoContent: e.target.value })}
                placeholder="Kami memahami bahwa keberhasilan sebuah event gathering..."
                className="w-full px-3.5 py-2.5 text-xs bg-white border border-slate-300 rounded-xl leading-relaxed text-slate-800"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div>
                <label className="text-xs font-bold text-slate-900 block mb-1">Teks Tombol Aksi:</label>
                <input
                  type="text"
                  value={whyConfig.ctaText || ''}
                  onChange={(e) => setWhyConfig({ ...whyConfig, ctaText: e.target.value })}
                  placeholder="Hubungi Konsultan Kami Sekarang"
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-bold text-slate-900"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-900 block mb-1">Subtext / Keterangan CTA:</label>
                <input
                  type="text"
                  value={whyConfig.ctaSubtext || ''}
                  onChange={(e) => setWhyConfig({ ...whyConfig, ctaSubtext: e.target.value })}
                  placeholder="Dapatkan proposal harga gratis dalam hitungan menit"
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl text-slate-800"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-900 block mb-1">
                Isi Template Pesan Otomatis WhatsApp saat Tombol CTA Diklik:
              </label>
              <textarea
                rows={2}
                value={whyConfig.ctaWaMessage || ''}
                onChange={(e) => setWhyConfig({ ...whyConfig, ctaWaMessage: e.target.value })}
                placeholder="Contoh: Halo Tim Konsultan, saya ingin berdiskusi dan menanyakan paket layanan serta keunggulan penawaran Anda."
                className="w-full px-3.5 py-2 text-xs bg-white border border-slate-300 rounded-xl leading-relaxed text-slate-800"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                *Pesan ini akan otomatis terisi pada aplikasi WhatsApp milik konsumen ketika mereka mengklik tombol CTA di banner bagian bawah halaman beranda.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
